import { isNativePlatform } from './platform';

/**
 * Cross-platform file export.
 *
 * Web: triggers the usual anchor download (unchanged behavior).
 * Native (Capacitor): the WebView has no filesystem / anchor-download, so we
 * write the file to the app cache and open the OS share sheet — the user can
 * then save it to Files, email it, etc.
 */

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Filesystem.writeFile wants the raw base64 payload, not the data-URL prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Save/share a generated file (PDF, CSV, ...). Accepts a Blob so callers keep
 * building content exactly as they do for the web download.
 */
export async function saveFile(
  filename: string,
  blob: Blob,
  mimeType?: string
): Promise<void> {
  if (isNativePlatform()) {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const { Share } = await import('@capacitor/share');

    const base64 = await blobToBase64(blob);
    // No `encoding` => Filesystem treats `data` as base64 and writes binary.
    const written = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });

    try {
      await Share.share({
        title: filename,
        url: written.uri,
        dialogTitle: 'Save or share file',
      });
    } catch (err: any) {
      // Dismissing the share sheet rejects; that's a normal user cancel, not a failure.
      if (!String(err?.message || '').toLowerCase().includes('cancel')) {
        throw err;
      }
    }
    return;
  }

  // Web: classic anchor download.
  const out = mimeType ? new Blob([blob], { type: mimeType }) : blob;
  const url = URL.createObjectURL(out);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
